import React, { useEffect, useMemo, useState } from 'react';
import AdminServiceOffers from '../components/forms/AdminServiceOffers';
import { Input } from '../components/ui/input-simple';
import { Button } from '../components/ui/button-simple';
import { servicesAPI } from '../services/api';
import { adminOffers as fallbackOffers } from '../data/mockData';
import { Star, Search, Download, RefreshCw, Briefcase, CheckCircle, XCircle, Eye, X, Trash2 } from 'lucide-react';
import { ConfirmDeleteModal } from '../components/modals/ConfirmDeleteModal';
import { Toast, useToast } from '../components/shared/Toast';

const STORAGE_KEY = 'bciis_admin_offers_v1';
const FEATURED_KEY = 'bciis_featured_services';

// Animations
const styles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-slide-down { animation: slideDown 0.6s ease-out forwards; }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-admin-services-animations]')) {
    styleSheet.setAttribute('data-admin-services-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export default function AdminServices() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [featuredIds, setFeaturedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FEATURED_KEY)) || []; } catch { return []; }
  });

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, service: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const { message, showSuccess, showError, dismiss } = useToast();

  const toggleFeatured = (id) => {
    setFeaturedIds(prev => {
      const next = prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id];
      localStorage.setItem(FEATURED_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleDeleteClick = (service) => {
    setDeleteModal({ isOpen: true, service });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.service) return;
    const id = deleteModal.service.id || deleteModal.service.service_id;
    setIsDeleting(true);
    try {
      if (servicesAPI && servicesAPI.delete) {
        await servicesAPI.delete(id);
      }
      setOffers(prev => prev.filter(o => (o.id || o.service_id) !== id));
      // Also update localStorage
      const updated = offers.filter(o => (o.id || o.service_id) !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      showSuccess('Service deleted successfully!');
    } catch (e) {
      showError('Failed to delete service. Please try again.');
    }
    setIsDeleting(false);
    setDeleteModal({ isOpen: false, service: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, service: null });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (servicesAPI && servicesAPI.getAll) {
          const res = await servicesAPI.getAll();
          const data = Array.isArray(res) ? res : (res?.data || res?.rows || []);
          setOffers(data);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          setLoading(false);
          return;
        }
      } catch (e) {
      }

      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try { setOffers(JSON.parse(stored)); }
        catch (e) { setOffers(fallbackOffers || []); }
      } else {
        setOffers(fallbackOffers || []);
      }
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return offers.filter(o => {
      if (!q) return true;
      return (o.title || o.service_name || '').toLowerCase().includes(q)
        || (o.description || o.service_description || '').toLowerCase().includes(q)
        || (o.contact || o.service_contact || '').toLowerCase().includes(q);
    });
  }, [offers, query]);

  const counts = useMemo(() => ({
    total: offers.length,
    active: offers.filter(o => o.is_active === 1 || o.active === true).length,
    inactive: offers.filter(o => !(o.is_active === 1 || o.active === true)).length,
  }), [offers]);

  const exportCsv = () => {
    if (!offers || offers.length === 0) return;
    const rows = offers.map(o => ({
      title: o.title || o.service_name || '',
      description: o.description || o.service_description || '',
      contact: o.contact || o.service_contact || '',
      campus: o.campus || '',
      active: (o.is_active === 1 || o.active === true) ? 'yes' : 'no'
    }));
    const header = Object.keys(rows[0]).join(',');
    const csv = [header, ...rows.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g,'""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'services_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const syncFromServer = async () => {
    setLoading(true);
    try {
      if (servicesAPI && servicesAPI.getAll) {
        const res = await servicesAPI.getAll();
        const data = Array.isArray(res) ? res : (res?.data || res?.rows || []);
        setOffers(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      }
    } catch (e) { /* sync failed silently */ }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <Toast message={message} onDismiss={dismiss} className="mb-6 animate-slide-down" />

        {/* Header with Gradient - matches AdminFarmersDirectory style */}
        <div className="mb-8 bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl shadow-xl p-5 text-white animate-slide-down">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <Briefcase className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">Manage Services</h1>
                <p className="text-amber-50 max-w-2xl text-sm">
                  Create, manage, and organize service offerings for BISU — Bilar Campus. Feature key services to highlight them for users.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <button
                onClick={exportCsv}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-amber-800 font-semibold text-sm rounded-xl shadow-lg hover:bg-amber-50 transition-all duration-200"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
              <button
                onClick={syncFromServer}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white font-semibold text-sm rounded-xl shadow-lg hover:bg-green-700 transition-all duration-200"
              >
                <RefreshCw className="h-4 w-4" />
                Sync
              </button>
            </div>
          </div>
        </div>

        {/* Stat Cards Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-5 hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-2.5 rounded-xl">
                <Briefcase className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total</p>
                <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-5 hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2.5 rounded-xl">
                <CheckCircle className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Active</p>
                <p className="text-2xl font-bold text-green-700">{counts.active}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-5 hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-3">
              <div className="bg-gray-100 p-2.5 rounded-xl">
                <XCircle className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Inactive</p>
                <p className="text-2xl font-bold text-gray-600">{counts.inactive}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-5 hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-2.5 rounded-xl">
                <Star className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Featured</p>
                <p className="text-2xl font-bold text-amber-700">{featuredIds.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Add / Edit Service Form */}
        <div className="mb-8">
          <AdminServiceOffers hideList />
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white p-6 rounded-2xl shadow-lg mb-8 border border-gray-200">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">Service Preview</h2>
            <span className="ml-auto text-sm text-gray-500">{filtered.length} of {offers.length} services</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by title, description or contact..."
                className="pl-10 h-11 rounded-xl border-gray-300 focus:border-amber-500 focus:ring-amber-500"
              />
            </div>
            {query && (
              <Button
                variant="outline"
                onClick={() => setQuery('')}
                className="flex items-center gap-2 rounded-xl hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mr-3"></div>
              <span className="text-gray-600">Loading services…</span>
            </div>
          )}

          {/* Empty State */}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12">
              <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No matching services found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your search or add a new service above.</p>
            </div>
          )}

          {/* Services Grid */}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(o => {
                const id = o.id || o.service_id;
                const title = o.title || o.service_name || 'Untitled';
                const desc = o.description || o.service_description || '';
                const contact = o.contact || o.service_contact || '';
                const isActive = o.is_active === 1 || o.active === true;
                const isFeatured = featuredIds.includes(id);

                return (
                  <div
                    key={id || title}
                    className="group bg-white rounded-2xl border border-gray-200 hover:border-amber-300 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col animate-fade-in-up"
                  >
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-5 py-3 border-b border-amber-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-bold text-amber-900 group-hover:text-amber-700 transition-colors line-clamp-1">{title}</h3>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => toggleFeatured(id)}
                            className="p-1.5 rounded-lg hover:bg-amber-200/60 transition-colors"
                            title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
                          >
                            <Star className={`w-4 h-4 transition-colors ${isFeatured ? 'text-amber-500 fill-amber-500' : 'text-gray-300 hover:text-amber-400'}`} />
                          </button>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm ${isActive ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>
                            {isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                          <button
                            onClick={() => handleDeleteClick(o)}
                            className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
                            title="Delete service"
                          >
                            <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600 transition-colors" />
                          </button>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5 flex-1 flex flex-col">
                      <p className="text-sm text-gray-600 leading-relaxed flex-1 line-clamp-3">
                        {desc || <span className="italic text-gray-400">No description provided</span>}
                      </p>
                      {contact && (
                        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
                          <div className="bg-amber-100 p-1.5 rounded-lg">
                            <Briefcase className="h-3.5 w-3.5 text-amber-700" />
                          </div>
                          <span className="text-xs text-gray-600 font-medium">{contact}</span>
                        </div>
                      )}
                      {isFeatured && (
                        <div className="mt-3 flex items-center gap-1.5">
                          <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                          <span className="text-xs font-semibold text-amber-600">Featured Service</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={deleteModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Service"
          message="Are you sure you want to delete this service? This action cannot be undone."
          itemName={deleteModal.service ? (deleteModal.service.title || deleteModal.service.service_name || '') : ''}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
